import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Link2, Save, Pencil } from "lucide-react";

const CATEGORIES = [
  { value: "para-clubs", label: "Para Clubs" },
  { value: "protocolos-de-seguridad", label: "Protocolos de Seguridad" },
  { value: "guias-de-lenguaje", label: "Guías prácticas de DDHH" },
];

const CLUB_GROUPS = [
  { value: "gestion-interna", label: "Gestión interna del Club" },
  { value: "proyectos-iniciativas", label: "Recursos para proyectos e iniciativas" },
  { value: "gestion-finanzas", label: "Gestión de Finanzas" },
];

const AdminResources = () => {
  const [category, setCategory] = useState("para-clubs");
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [group, setGroup] = useState(CLUB_GROUPS[0].value);
  const [kind, setKind] = useState("file");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [meta, setMeta] = useState({});
  const [savingMeta, setSavingMeta] = useState(false);

  const loadDocs = (cat) => api.get(`/resources/${cat}`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  const loadMeta = (cat) => api.get(`/resource-meta/${cat}`).then((r) => setMeta(r.data || {})).catch(() => setMeta({}));

  useEffect(() => { loadDocs(category); loadMeta(category); }, [category]);

  const resetForm = () => { setEditingId(null); setTitle(""); setFile(null); setUrl(""); setKind("file"); setGroup(CLUB_GROUPS[0].value); };

  const startEdit = (d) => {
    setEditingId(d.id);
    setTitle(d.title);
    setKind(d.kind === "link" ? "link" : "file");
    setUrl(d.url || "");
    setGroup(d.group || CLUB_GROUPS[0].value);
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!editingId && kind === "file" && !file) { toast.error("Selecciona un archivo (PDF o DOCX)"); return; }
    if (kind === "link" && !url.trim()) { toast.error("Escribe el enlace"); return; }
    const fd = new FormData();
    fd.append("title", title);
    if (category === "para-clubs") fd.append("group", group);
    if (kind === "file") { if (file) fd.append("file", file); }
    else fd.append("url", url.trim());
    setUploading(true);
    try {
      if (editingId) {
        await api.put(`/resources/doc/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Recurso actualizado");
      } else {
        await api.post(`/resources/${category}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Recurso publicado");
      }
      resetForm();
      loadDocs(category);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar el recurso"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este recurso?")) return;
    await api.delete(`/resources/doc/${id}`);
    toast.success("Recurso eliminado");
    loadDocs(category);
  };

  const saveMeta = async (g) => {
    setSavingMeta(true);
    try {
      await api.put(`/resource-meta`, { category, group: g, description: meta[g] || "" });
      toast.success("Descripción guardada");
    } catch { toast.error("No se pudo guardar la descripción"); }
    finally { setSavingMeta(false); }
  };

  const groupLabel = (v) => CLUB_GROUPS.find((g) => g.value === v)?.label || "";

  return (
    <div className="mt-8">
      <div className="flex gap-2 flex-wrap mb-6">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            data-testid={`res-cat-${c.value}`}
            onClick={() => setCategory(c.value)}
            className={`px-4 py-2 rounded-sm text-sm font-semibold border ${category === c.value ? "bg-brand-magenta text-white border-brand-magenta" : "bg-white border-[#E5E5E5] text-brand-ink"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Descripciones por sección (solo Para Clubs) */}
      {category === "para-clubs" && (
        <div className="bg-white border border-[#E5E5E5] rounded-sm p-6 mb-8">
          <h2 className="font-heading text-lg font-bold text-brand-ink">Descripciones de las secciones</h2>
          <p className="mt-1 text-sm text-brand-ink/60">Un texto breve que aparece bajo el título de cada sección en la página pública.</p>
          <div className="mt-4 space-y-4">
            {CLUB_GROUPS.map((g) => (
              <div key={g.value}>
                <label className="text-sm font-semibold text-brand-ink">{g.label}</label>
                <div className="mt-1.5 flex gap-2">
                  <textarea
                    rows={2}
                    data-testid={`meta-${g.value}`}
                    value={meta[g.value] || ""}
                    onChange={(e) => setMeta({ ...meta, [g.value]: e.target.value })}
                    className="flex-1 border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm outline-none focus:border-brand-magenta"
                  />
                  <button data-testid={`save-meta-${g.value}`} onClick={() => saveMeta(g.value)} disabled={savingMeta} className="inline-flex items-center gap-1.5 bg-brand-ink text-white text-sm font-semibold px-3 rounded-sm hover:bg-brand-magenta disabled:opacity-60">
                    <Save size={15} /> Guardar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <form onSubmit={upload} data-testid="resource-form" className="bg-white border border-[#E5E5E5] rounded-sm p-6 h-fit">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2">
              <Upload size={18} className="text-brand-magenta" /> {editingId ? "Editar recurso" : "Nuevo recurso"}
            </h2>
            {editingId && <button type="button" onClick={resetForm} data-testid="resource-cancel" className="text-sm text-brand-ink/60 hover:text-brand-ink">Cancelar</button>}
          </div>

          <div className="mt-4 flex gap-2">
            <button type="button" data-testid="res-kind-file" disabled={!!editingId} onClick={() => setKind("file")} className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-sm font-semibold border disabled:opacity-50 ${kind === "file" ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-[#E5E5E5] text-brand-ink"}`}>
              <FileText size={15} /> Archivo (PDF/DOCX)
            </button>
            <button type="button" data-testid="res-kind-link" disabled={!!editingId} onClick={() => setKind("link")} className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-sm font-semibold border disabled:opacity-50 ${kind === "link" ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-[#E5E5E5] text-brand-ink"}`}>
              <Link2 size={15} /> Enlace
            </button>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-sm font-semibold text-brand-ink">Título</label>
              <input required data-testid="resource-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
            </div>
            {category === "para-clubs" && (
              <div>
                <label className="text-sm font-semibold text-brand-ink">Sección</label>
                <select data-testid="resource-group" value={group} onChange={(e) => setGroup(e.target.value)} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta bg-white">
                  {CLUB_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            )}
            {kind === "file" ? (
              <div>
                <label className="text-sm font-semibold text-brand-ink">Archivo (PDF o DOCX){editingId ? " — opcional (déjalo vacío para conservar el actual)" : ""}</label>
                <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" data-testid="resource-file" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 w-full text-sm border border-[#E5E5E5] rounded-sm px-4 py-2.5 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-brand-ink file:text-white" />
              </div>
            ) : (
              <div>
                <label className="text-sm font-semibold text-brand-ink">Enlace (URL)</label>
                <input type="url" data-testid="resource-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
              </div>
            )}
          </div>
          <button type="submit" disabled={uploading} data-testid="upload-resource-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-magenta text-white font-semibold py-3 rounded-sm hover:bg-brand-orange transition-colors disabled:opacity-60">
            {uploading ? "Guardando…" : editingId ? "Guardar cambios" : "Publicar recurso"}
          </button>
        </form>

        <div>
          <h2 className="font-heading text-lg font-bold text-brand-ink mb-4">Recursos publicados</h2>
          {docs.length === 0 ? (
            <p className="text-brand-ink/50">Aún no hay recursos en esta categoría.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {docs.map((d) => (
                <div key={d.id} data-testid={`admin-resource-${d.id}`} className="bg-white border border-[#E5E5E5] rounded-sm p-4 flex items-center gap-3">
                  {d.kind === "link" ? <Link2 size={20} className="text-brand-magenta flex-shrink-0" /> : <FileText size={20} className="text-brand-magenta flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-brand-ink truncate">{d.title}</h3>
                    <p className="text-xs text-brand-ink/50">
                      <span className="uppercase font-semibold">{d.kind}</span>{d.group ? ` · ${groupLabel(d.group)}` : ""}
                    </p>
                  </div>
                  <button data-testid={`edit-resource-${d.id}`} onClick={() => startEdit(d)} className="p-2 text-brand-ink hover:text-brand-magenta"><Pencil size={16} /></button>
                  <button data-testid={`delete-resource-${d.id}`} onClick={() => remove(d.id)} className="p-2 text-brand-ink hover:text-red-600"><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResources;
